import { useMobileScreen } from "@/shared/hooks/useMobileScreen";
import { getTextLogo } from "@/shared/utils";
import {
  Anchor,
  Box,
  Flex,
  Group,
  Image,
  ScrollArea,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { ReactNode } from "react";
import { AUTH_PANEL_LIGHT_BG } from "@/design-system/tokens";
import { RoutePaths } from "@/core/constants/RoutePaths";
interface AuthLayoutProps {
  title: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  /**
   * Ações fixas (ex.: botão de submit) ancoradas fora da área rolável, junto do
   * footer. Ficam sempre visíveis, sem serem empurradas pelo crescimento do
   * conteúdo do formulário (ex.: medidor de força da senha no signup).
   */
  actions?: ReactNode;
  leftPanel?: ReactNode;
  hideLeftPanelOnMobile?: boolean;
}

const signInImageUrl = "/images/signin-image.jpg";
const signInImageDesktopAvifUrl = "/images/signin-image-desktop.avif";

export const AuthLayout = ({
  title,
  children,
  footer,
  actions,
  leftPanel,
  hideLeftPanelOnMobile = false,
}: AuthLayoutProps) => {
  const { colorScheme } = useMantineColorScheme();
  const { isMobileScreen } = useMobileScreen();
  const logo = getTextLogo(colorScheme);
  const theme = useMantineTheme();

  const background =
    colorScheme === "dark"
      ? `linear-gradient(117deg, ${theme.colors["background_3"][0]} 4.48%, ${theme.colors["P900"][0]} 94.25%)`
      : theme.colors["background_3"][0];

  const contentBg =
    colorScheme === "dark"
      ? "var(--mantine-color-background_2-0)"
      : AUTH_PANEL_LIGHT_BG;

  const paddingSize = isMobileScreen ? 24 : "min(max(6%, 24px), 48px)";

  return (
    <Flex
      direction={"row"}
      align={"center"}
      justify={"center"}
      bg={background}
      h={"100vh"}
      w={"100%"}
      style={{ overflow: "hidden" }}>
      <Flex
        direction={isMobileScreen ? "column-reverse" : "row"}
        w={"100%"}
        h={"100%"}
        miw={300}
        style={{
          overflow: "hidden",
        }}>
        {/* --- LADO ESQUERDO --- */}
        {!(isMobileScreen && hideLeftPanelOnMobile) && (
          <Box
            w={isMobileScreen ? "100%" : "50%"}
            h={isMobileScreen ? 250 : "100%"}
            bg="gray.2"
            style={{
              position: "relative",
              overflow: "hidden",
              transition: "background-color 0.3s ease",
            }}>
            {leftPanel ? (
              leftPanel
            ) : isMobileScreen ? (
              <Flex
                h="100%"
                w="100%"
                align="flex-end"
                justify="flex-start"
                p={24}
                style={{
                  background:
                    colorScheme === "dark"
                      ? `linear-gradient(145deg, ${theme.colors["P900"][0]} 0%, ${theme.colors["background_2"][0]} 100%)`
                      : `linear-gradient(145deg, ${theme.colors["R500"][0]} 0%, ${theme.colors["background_3"][0]} 100%)`,
                }}>
                <Text
                  c="white"
                  ff="heading"
                  fz={28}
                  fw={700}
                  maw={220}
                  lh={1.05}>
                  Crie sua conta e comece em segundos.
                </Text>
              </Flex>
            ) : (
              <picture>
                <source
                  srcSet={signInImageDesktopAvifUrl}
                  type="image/avif"
                />
                <img
                  src={signInImageUrl}
                  alt="Escritório com pessoas trabalhando"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </picture>
            )}
          </Box>
        )}

        {/* --- LADO DIREITO --- */}
        {/* `main`: o formulário é o conteúdo principal da tela (landmark para
            leitor de tela; o Lighthouse reprovava a página sem nenhum). */}
        <Flex
          component="main"
          direction={"column"}
          w={isMobileScreen ? "100%" : "50%"}
          bg={contentBg}
          h={"100%"}
          p={paddingSize}
          justify={"center"}
          align={"center"}
          style={{ position: "relative" }}>
          <Flex
            direction="column"
            w="100%"
            maw={480}
            mah="100%"
            style={{
              overflow: "hidden",
            }}>
            <ScrollArea
              h={"auto"}
              w="100%"
              flex={"0 1 auto"}
              type="hover"
              scrollbarSize={6}
              offsetScrollbars
              viewportProps={{
                style: { display: "flex", flexDirection: "column" },
              }}>
              <Flex
                direction="column"
                pb={0}>
                {/* width/height reservam a proporção do SVG (277×137) antes de
                    ele carregar; o tamanho na tela continua vindo de `w`. */}
                <Image
                  w={104}
                  h="auto"
                  width={104}
                  height={51}
                  src={logo}
                  alt="Logo do Super Agents"
                  mb={isMobileScreen ? 24 : "min(max(6%, 24px), 48px)"}
                />

                {title}

                <Flex
                  direction={"column"}
                  w={"100%"}
                  align={"center"}
                  justify={"center"}>
                  {children}
                </Flex>
              </Flex>
            </ScrollArea>

            {/* REGIÃO FIXA (ações + footer) — fora do ScrollArea, então o
                botão de submit fica sempre visível e não é empurrado pelo
                crescimento do formulário. */}
            <Box
              pt={24}
              w={"100%"}
              bg={contentBg}
              style={{
                zIndex: 10,
                flexShrink: 0,
              }}>
              <Flex
                direction="column"
                w="100%"
                gap={16}>
                {actions}
                {footer}

                {/* Links legais em TODAS as telas de autenticação. Ficam aqui, no
                    layout, e não em cada página: login, signup, recuperação de
                    senha e convite compartilham este rodapé, e repetir o bloco
                    em cada uma garantiria que alguma ficasse para trás.

                    Abrem em nova aba para não descartar o que já foi digitado
                    no formulário. */}
                <Group
                  justify="center"
                  gap={8}
                  pb={8}>
                  <Anchor
                    href={RoutePaths.TERMS_OF_USE}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="xs"
                    c="dimmed">
                    Termos de Uso
                  </Anchor>
                  <Text
                    size="xs"
                    c="dimmed"
                    aria-hidden="true">
                    ·
                  </Text>
                  <Anchor
                    href={RoutePaths.PRIVACY_POLICY}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="xs"
                    c="dimmed">
                    Política de Privacidade
                  </Anchor>
                </Group>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
